import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { audience } from '@bb/contracts';
import type { AdminApi } from '../../api/client.ts';

type Kind = audience.MessageTarget['kind'];
type Target = { kind: Kind; id: string };

const KIND_LABEL: Record<Kind, string> = { campaign: 'Campaign', canvas: 'Canvas', canvas_step: 'Canvas step', tag: 'Any campaign or Canvas tagged' };
const ID_LABEL: Record<Exclude<Kind, 'tag' | 'campaign'>, string> = { canvas: 'Canvas id', canvas_step: 'step id' };
const STATUS_LABEL: Record<string, string> = { draft: 'Draft', scheduled: 'Scheduled', active: 'Active', idle: 'Idle', paused: 'Paused', stopped: 'Stopped', completed: 'Completed', archived: 'Archived' };

interface Props {
  label: string;
  value: Target | undefined;
  kinds: readonly Kind[];
  environmentId: string;
  readOnly: boolean;
  onChange: (target: Target) => void;
}

/**
 * The message a retargeting filter looks at (BB-43): a campaign, a Canvas, one Canvas step, or everything with a tag.
 * Tags come from the registry (BB-18); campaigns are picked by name with their status, archived ones marked (BB-96);
 * Canvases and steps are entered by id until the Canvas list exists (E09).
 */
export function MessageTargetEditor({ label, value, kinds, environmentId, readOnly, onChange }: Props) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const target = value ?? { kind: kinds[0]!, id: '' };
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId), enabled: target.kind === 'tag' });
  const campaigns = useQuery({ queryKey: ['campaigns', environmentId, 'picker'], queryFn: () => api.listCampaigns(environmentId, { status: 'all', sort: 'name', limit: 200 }), enabled: target.kind === 'campaign' });
  const campaignItems = campaigns.data?.items ?? [];
  return (
    <span role="group" aria-label={`${label}: message`}>
      <select aria-label={`${label}: message kind`} value={target.kind} disabled={readOnly} onChange={(e) => onChange({ kind: e.target.value as Kind, id: '' })}>
        {kinds.map((k) => (
          <option key={k} value={k}>
            {KIND_LABEL[k]}
          </option>
        ))}
      </select>
      {target.kind === 'tag' ? (
        <select aria-label={`${label}: tag`} value={target.id} disabled={readOnly} onChange={(e) => onChange({ ...target, id: e.target.value })}>
          {!target.id && <option value="">Choose a tag</option>}
          {(tags.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      ) : target.kind === 'campaign' ? (
        <select aria-label={`${label}: campaign`} value={target.id} disabled={readOnly} onChange={(e) => onChange({ ...target, id: e.target.value })}>
          {!target.id && <option value="">{campaigns.isPending ? 'Loading campaigns…' : 'Choose a campaign'}</option>}
          {target.id && !campaigns.isPending && !campaignItems.some((c) => c.id === target.id) && <option value={target.id}>Unknown campaign {target.id}</option>}
          {campaignItems.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {STATUS_LABEL[c.idle ? 'idle' : c.status]}
            </option>
          ))}
        </select>
      ) : (
        <input
          aria-label={`${label}: ${ID_LABEL[target.kind]}`}
          value={target.id}
          disabled={readOnly}
          spellCheck={false}
          placeholder="ID from its page"
          onChange={(e) => onChange({ ...target, id: e.target.value.trim() })}
        />
      )}
    </span>
  );
}
